import { Link } from 'react-router-dom';

// Franchisee-facing announcement for the October 2026 Marketing in a Box order
// window. Static content, mirroring the memo marketing sent to Franchise
// Partners — the dashboard banner (MiabOrderBanner) links here.
//
// This is deliberately a plain page rather than a DB-backed announcement: the
// content is fixed for one order window and the banner that points at it
// retires itself. If announcements become a recurring per-month need, promote
// this into a table like miab_surveys rather than cloning the file.

const STORE_URL = 'https://www.theamadastore.com/';
const MARKETING_EMAIL = 'marketing@amadafranchise.com';

type KitItem = {
  name: string;
  qty: string;
  blurb: string;
};

const KIT_ITEMS: KitItem[] = [
  {
    name: 'Large Print Deluxe 3-in-1 Puzzle Book (Volume 2)',
    qty: '50',
    blurb:
      'Engaging entertainment item for recipients — word search, crossword and Sudoku in a large, easy-to-read format. Branded with the navy Amada logo.',
  },
  {
    name: 'Fuzzy Feet Slipper Socks',
    qty: '25 pairs',
    blurb:
      'Comfortable, cozy branded apparel. Navy socks embroidered with the Amada hands logo in navy.',
  },
  {
    name: 'Bone / Skeleton Finger Ballpoint Pens',
    qty: '50',
    blurb:
      'A fun novelty item, ideal for healthcare themes or general interest. A variety pack of bone-shaped pens carrying the full Amada logo in navy.',
  },
  {
    name: 'Round Cork Coasters',
    qty: '25 packs of 2',
    blurb:
      'Eco-friendly, practical desk accessory. Packed in sets of two, with the full logo printed in brown on natural cork.',
  },
  {
    name: 'Reusable Fold-Up Shopping Tote Bag',
    qty: '50',
    blurb:
      'Compact and convenient multi-use bag, 14.96" W x 22.83" H. White tote with a full-colour imprint.',
  },
  {
    name: 'Seasonal Stickers (2 designs)',
    qty: '50 of each',
    blurb:
      'Festive decorative additions. Die-cut stickers carrying your phone number, featuring Halloween and World Stroke Day designs.',
  },
  {
    name: 'Halloween-Themed Flatpack Goodie Box (empty)',
    qty: '50',
    blurb:
      'Seasonal packaging to house promotional items or treats for the recipients in your territory.',
  },
];

export default function MiabOctoberAnnouncement() {
  return (
    <div className="announcement">
      <div className="announcement-inner">
        <Link to="/" className="announcement-back">
          ← Back to dashboard
        </Link>

        <header className="announcement-header">
          <span className="announcement-badge">Marketing in a Box</span>
          <h1>October Marketing in a Box is available to order</h1>
          <p className="announcement-lede">
            Orders are filled on a first-come, first-served basis — place yours early.
          </p>
        </header>

        <img
          className="announcement-hero"
          src="/static/miab/oct-2026/october-box-hero.webp"
          alt="The October Marketing in a Box kit: tote bag, bone-shaped pens, Halloween goodie box, puzzle book, cork coaster, fuzzy slipper socks and Halloween and World Stroke Day stickers."
        />

        <section className="announcement-item">
          <p>Franchise Partners,</p>
          <p>
            October&rsquo;s <strong>Marketing in a Box</strong> is now available to order. You can
            visit{' '}
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
              TheAmadaStore.com
            </a>{' '}
            to place your order today. Orders will be filled on a{' '}
            <strong>first-come, first-served basis</strong>. A description of everything included is
            below.
          </p>
          <p>
            If you have questions, feel free to reach out to Marketing by emailing{' '}
            <a href={`mailto:${MARKETING_EMAIL}`}>{MARKETING_EMAIL}</a>.
          </p>
          <p className="announcement-cta-row">
            <a
              className="announcement-cta"
              href={STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Order at TheAmadaStore.com
            </a>
          </p>
        </section>

        <section className="announcement-item">
          <h2>What&rsquo;s in the kit</h2>
          <p className="announcement-kit-name">Kit name: October Marketing in a Box</p>
          <ul className="announcement-kit">
            {KIT_ITEMS.map((item) => (
              <li key={item.name}>
                <span className="announcement-kit-item">{item.name}</span>
                <span className="announcement-kit-qty">{item.qty}</span>
                <p>{item.blurb}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="announcement-item">
          <h2>Fulfillment &amp; shipping schedule</h2>
          <p>
            <strong>Main shipment:</strong> ships the week of September 1st.
          </p>
          <p>
            <strong>Secondary shipment:</strong> the cork coasters and bone pens arrive separately,
            about two weeks later.
          </p>
          <p className="announcement-note">
            The second shipment was scheduled separately to ensure high quality and product variety
            — nothing is missing from your order if the first box arrives without those two items.
          </p>
        </section>

        <footer className="announcement-footer">
          <Link to="/" className="announcement-back">
            ← Back to dashboard
          </Link>
        </footer>
      </div>
    </div>
  );
}
